'use client';

import { TypingIndicator } from '@/components/common/TypingIndicator';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { signOut, useSession } from 'next-auth/react';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

export default function GuestHome() {
  const { data: session, status } = useSession();

  const [revealedCount, setRevealedCount] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const [showActions, setShowActions] = useState(false);

  const [steps, setSteps] = useState(['greetNew']);

  const messagesEndRef = useRef(null);

  // store timeout ids so we can clear them when component unmounts or step changes
  const timersRef = useRef([]);

  const timerRef = useRef(null);

  // You can tweak these to control the pacing / animation timing
  const TYPING_DURATION = 800; // ms: how long typing indicator shows before revealing a message
  const BETWEEN_MESSAGES_DELAY = 300;

  useEffect(() => {
    // whenever steps change, reveal messages for the last step with delays and typing animation
    // clear any pending timers first
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];

    const lastStep = steps[steps.length - 1];
    const stepData = flow[lastStep];

    if (!stepData || !Array.isArray(stepData.messages)) {
      setRevealedCount(0);
      setIsTyping(false);
      return;
    }

    // start fresh
    setRevealedCount(0);
    setIsTyping(true);

    const messagesCount = stepData.messages.length;

    // schedule events: for each message, first show typing for TYPING_DURATION, then reveal it
    let scheduleBase = 0;
    for (let i = 0; i < messagesCount; i++) {
      // start typing for message i
      const startTypingAt = scheduleBase;
      const typingTimer = setTimeout(() => {
        setIsTyping(true);
      }, startTypingAt);
      timersRef.current.push(typingTimer);

      // reveal message after typing duration
      const revealAt = startTypingAt + TYPING_DURATION;
      const revealTimer = setTimeout(() => {
        setRevealedCount((prev) => prev + 1);
        setIsTyping(false);
      }, revealAt);
      timersRef.current.push(revealTimer);

      // prepare next base: after reveal, wait a bit before starting typing for next message
      scheduleBase = revealAt + BETWEEN_MESSAGES_DELAY;
    }

    // After all messages revealed, ensure typing is false and optionally show actions
    const finishTimer = setTimeout(() => {
      setIsTyping(false);
      setShowActions(true);
    }, scheduleBase);
    timersRef.current.push(finishTimer);

    //

    // cleanup on unmount or next run
    return () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };
  }, [steps]);

  // Add this effect to handle auto-scrolling
  useEffect(() => {
    if (steps <= 2) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [revealedCount, steps]);

  const flow = {
    greetNew: {
      messages: [
        <>
          {session
            ? 'Would you like to focus on'
            : 'Explore SmartGuy freely. Click any button to access videos, blogs, music, and resources with no account required. Create a free account to unlock Self-Tests, track your progress and watch yourself grow.'}
        </>,
      ],
      options: session
        ? [
            {
              label: 'Improve Yourself',
              next: 'improveYourself',
            },
            {
              label: 'Watch videos',
              ...(session ? { url: '/chat' } : { next: 'watchVideos' }),
            },
            { label: 'Browse blogs', url: '/blogs' },
            // {
            //   label: 'Find a Local Business',
            //   next: 'findBusiness',
            //   url: '/professional?active=search',
            // },
            // { label: 'Add your Business', next: 'findBusiness', url: '/professional?active=signup' },
          ]
        : [
            { label: 'Sign In', url: '/signin' },
            { label: 'Sign Up', next: '', url: '/signup' },
            /* {
              label: 'Faith',
              next: 'faith',
            },
            {
              label: 'Self Improvement',
              next: 'improveYourself',
            },
            {
              label: 'Business',
              next: 'business',
            }, */
          ],
    },

    personal: {
      messages: [
        <>
          {session
            ? 'Would you like to focus on'
            : 'Great. First, let’s get your logged in or if you haven’t yet, signed up…'}
        </>,
      ],
      options: session
        ? [
            {
              label: 'Improve Yourself',
              next: 'improveYourself',
            },
            {
              label: 'Watch videos',
              ...(session ? { url: '/chat' } : { next: 'watchVideos' }),
            },
            { label: 'Browse blogs', url: '/blogs' },
            // {
            //   label: 'Find a Local Business',
            //   next: 'findBusiness',
            //   url: '/professional?active=search',
            // },
            // { label: 'Add your Business', next: 'findBusiness', url: '/professional?active=signup' },
          ]
        : [
            { label: 'Sign In', url: '/signin' },
            { label: 'Sign Up', next: '', url: '/signup' },
            /* {
              label: 'Faith',
              next: 'faith',
            },
            {
              label: 'Self Improvement',
              next: 'improveYourself',
            },
            {
              label: 'Business',
              next: 'business',
            }, */
          ],
    },

    faith: {
      messages: [<>Would you like ...</>],
      options: [
        {
          label: 'Videos',
          next: 'videos',
        },
        { label: 'Blogs', next: 'blogs' },
      ],
    },

    videos: {
      messages: [<>Would you like to focus on…</>],
      options: [
        {
          label: 'Character Development',
          url: '/chat?category=faith&subCategory=character-development',
        },
        {
          label: 'Interactions with Others',
          url: '/chat?category=faith&subCategory=character-development',
        },
        { label: 'Moral Integrity', url: '/chat?category=faith&subCategory=moral-integrity' },
        {
          label: 'Witnessing and Evangelism',
          url: '/chat?category=faith&subCategory=witnessing-and-evangelism',
        },
        { label: 'Holidays', url: '/chat?category=faith&subCategory=holidays' },
        { label: 'Relationship', url: '/chat?category=faith&subCategory=relationship' },
        { label: 'OCIA Classes', next: 'ociaClasses' },
      ],
    },

    ociaClasses: {
      messages: [
        <>
          Welcome. You are about to begin a 30 video journey designed to help you personally
          encounter Jesus Christ, understand the foundations of the Catholic faith, and learn how to
          live fully as an active member of His Church Would you like ...
        </>,
      ],
      options: [
        {
          label: 'Phase 1: Inquiry ( 1–5)',
          url: '/chat?category=faith&subCategory=ocia-classes&type=phase-1',
        },
        {
          label: 'Phase 2: Catechumenate ( 6–18)',
          url: '/chat?category=faith&subCategory=ocia-classes&type=phase-2',
        },
        {
          label: 'Phase 3: Purification & Enlightenment ( 19–24)',
          url: '/chat?category=faith&subCategory=ocia-classes&type=phase-3',
        },
        {
          label: 'Phase 4: Mystagogy ( 25–30)',
          url: '/chat?category=faith&subCategory=ocia-classes&type=phase-4',
        },
      ],
    },

    blogs: {
      messages: [<>Would you like ...</>],
      options: [
        {
          label: 'Faith in God',
          url: '/blogs?category=faith-in-god',
        },
        {
          label: 'Fellowship',
          url: '/blogs?category=fellowship',
        },
        {
          label: 'OCIA Classes',
          url: '/blogs?category=ocia-classes',
        },
      ],
    },

    improveYourself: {
      messages: [
        <>
          I’m glad you’re here - most people never reach their true potential, not because they
          can’t, but because life buries who they really are, and you’re here because you’re ready
          to reclaim clarity, confidence, and control.
        </>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'What is this?', next: 'whatIsThis' },
      ],
    },
    whatIsThis: {
      messages: [
        <>
          Great question. SmartGuy is a simple personal growth system that helps you become the
          strongest, clearest, most confident version of yourself.
        </>,
        <>
          Instead of guessing or chasing random advice, SmartGuy shows you who you really are,
          what's holding you back, and exactly how to fix it - then guides you with short, powerful
          videos for every area of life.
        </>,
        <>
          Think of me as your personal advisor for mindset, confidence, discipline, relationships,
          career, and emotional strength.
        </>,
        <>Want to see how it works?</>,
      ],
      options: [
        { label: 'Yes - Show Me', next: 'yesShowMe' },
        { label: 'Not Right Now', next: '', url: '/signin' },
      ],
    },
    yesShowMe: {
      messages:
        session?.user?.progress === 'assessment/friends-family'
          ? [
              <>
                Perfect. Your assessment has two parts:
                <ol className="list-disc">
                  <li>
                    <b>How others see you</b> - because the people around you notice strengths and
                    weaknesses you might miss
                  </li>
                  <li>How you see yourself</li> - your internal view
                </ol>
              </>,
              <>
                Together, they give you a clear snapshot of who you are - your strengths, your
                weaknesses, and the hidden habits shaping your results…
              </>,
              <>Click below to send the short questionnaire to friends and family.</>,
            ]
          : [
              <>
                <b>SmartGuy</b>:<br />
                Here’s the simple 3-step journey:
                <br />
                <br />
                <ol className="list-decimal px-4">
                  <li>
                    Take Your Personality Reset Assessment…This identifies your strengths and the
                    hidden weaknesses others see but you don’t…
                  </li>
                  <li>
                    Complete Your Reset Tasks…These tasks help you break damaging patterns and turn
                    your weaknesses into strengths…
                  </li>
                  <li>
                    Grow With SmartGuy… Once your reset is complete, you unlock SmartGuy’s entire
                    library of videos, skills, tools, and guidance for life.{' '}
                  </li>
                </ol>
              </>,
              <>Ready to begin?</>,
            ],
      options:
        session?.user?.progress === 'assessment/friends-family'
          ? [{ label: 'Send Questionnaire', url: '/assessment/friends-family' }]
          : [
              { label: 'Yes - Start My Journey', next: 'startMyJourney' },
              { label: 'I Have Another Question', next: 'iHaveAnotherQuestion' },
            ],
    },
    iHaveAnotherQuestion: {
      messages: [
        <>
          <b>SmartGuy</b>:<br />
          Of course. What would you like to know?
        </>,
        <>Here are some questions people ask before starting:</>,
      ],
      options: [
        { label: 'Is this really free?', next: 'isThisReallyFree' },
        { label: 'How long does the program take?', next: 'howLongDoesTheProgramTake' },
        { label: 'Who created SmartGuy?', next: 'whoCreatedSmartGuy' },
        { label: 'Will this actually help me?', next: 'willThisActuallyHelpMe' },
        { label: 'What do I have to do?', next: 'whatDoIHaveToDo' },
      ],
    },
    isThisReallyFree: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          Yes. The program and personality assessments are completely free.
        </>,
        <>
          Why?
          <br />
          Because everyone deserves the chance to break old patterns, rebuild their confidence, and
          become who they were meant to be - not just people who can afford expensive programs.
        </>,
        <>You can explore, learn, and grow without paying anything.</>,
        <>Would you like to begin?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    howLongDoesTheProgramTake: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          You can go at your own pace. Most people complete their assessment in a few days, but some
          like to take it slower.
        </>,
        <>There’s no pressure.</>,
        <>
          After the Reset, you unlock tools, videos, and guidance you can use for the rest of your
          life.
        </>,
        <>Do you want to get started now?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    whoCreatedSmartGuy: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          SmartGuy was built by a team of educators, entrepreneurs, psychologists, and faith-driven
          thinkers who wanted to create a simple way for anyone to rebuild their life - from the
          inside out.
        </>,
        <>
          No gurus. No complicated theories. Just practical tools, clear guidance, and the kind of
          training only the elite used to receive.
        </>,
        <>Would you like to see how it works?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    willThisActuallyHelpMe: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          Yes - if you go through it honestly.
        </>,
        <>
          The assessment reveals the strengths you can build on and the weaknesses that have quietly
          shaped your life…
        </>,
        <>The Reset Tasks show you exactly how to fix them…</>,
        <>
          And the videos give you ongoing guidance for confidence, clarity, career, relationships,
          discipline, emotional strength, and spiritual grounding…
        </>,
        <>
          People who complete the Reset almost always say the same thing:
          <br />
          “I finally understand myself. I finally feel in control.”
        </>,
        <>Want to start?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    whatDoIHaveToDo: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          It’s simple.
          <br />
          <ol className="list-decimal px-4">
            <li>Take the personality assessment</li>
            <li>Review your strengths and weaknesses</li>
            <li>Complete your personalized Reset Tasks</li>
            <li>Grow with SmartGuy’s tools, videos, and guidance</li>
          </ol>
        </>,
        <>No complicated steps. I guide you through everything.</>,
        <>Ready to begin?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    askAnotherQuestion: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          No problem. Here are more questions people ask:
        </>,
      ],
      options: [
        { label: 'Is my information private?', next: 'isMyInformationPrivate' },
        {
          label: 'What makes this different from therapy?',
          next: 'whatMakesThisDifferentFromTherapy',
        },
        { label: 'How accurate is the assessment?', next: 'howAccurateIsTheAssessment' },
        { label: 'Can this help with confidence?', next: 'canThisHelpWithConfidence' },
        { label: 'Can I skip to the videos?', next: 'canISkipToTheVideos' },
      ],
      notes: [<>(I can create responses for these as well if you want.)</>],
    },
    isMyInformationPrivate: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          Yes. Your information is private and used only to guide your growth.
        </>,
        <>
          Your assessment results are yours alone. Nothing is shared publicly, and your data is not
          sold or used for advertising.
        </>,
        <>
          The only people who see anything are the friends or family you personally choose to email
          for feedback.
        </>,
        <>Does that help?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    whatMakesThisDifferentFromTherapy: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          Therapy focuses on healing past wounds. SmartGuy focuses on rebuilding your identity,
          strengths, and future.
        </>,

        <>
          Therapy helps you understand why you are the way you are. SmartGuy helps you become who
          you’re capable of being.
        </>,
        <>
          Many people use both. But SmartGuy is designed to be practical, fast, and transformational
          - a structured path that builds confidence, clarity, discipline, emotional strength, and
          purpose.
        </>,
        <>Would you like to see how it works?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    howAccurateIsTheAssessment: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          Very accurate - and here’s why:
        </>,
        <>Most personality tests rely only on your self-perception, which is often biased.</>,
        <>
          The SmartGuy Assessment combines <b>your own answers</b> with <b>how others see you</b>,
          giving you a more complete picture of your strengths, blind spots, and patterns.
        </>,
        <>
          People are often surprised by how clearly it reveals the traits they’ve never noticed in
          themselves.
        </>,
        <>Ready to try it?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    canThisHelpWithConfidence: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          Absolutely. Confidence is one of the main reasons this program was created.
        </>,
        <>
          SmartGuy helps you:
          <br />
          <ol className="list-decimal px-4">
            <li>Remove the programming that made you doubt yourself</li>
            <li>Strengthen your identity and inner voice</li>
            <li>Build discipline and emotional control</li>
            <li>Replace old patterns with new habits</li>
            <li>Reconnect to the version of you God designed</li>
          </ol>
        </>,
        <>Most people feel more confident before they even finish the Reset Tasks.</>,
        <>Would you like to start building your confidence?</>,
      ],
      options: [
        { label: 'Yes - Start My Journey', next: 'startMyJourney' },
        { label: 'Ask Another Question', next: 'askAnotherQuestion' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    canISkipToTheVideos: {
      messages: [
        <>
          <b>SmartGuy:</b>
          <br />
          You can, but it’s not recommended.
        </>,

        <>
          The videos are powerful, but the Reset works best when you first understand your
          personality blueprint - your strengths, weaknesses, blind spots, and patterns.
        </>,

        <>
          Once you complete the assessment, I can guide you through videos tailored to your
          personality.
        </>,

        <>Still want to skip ahead?</>,
      ],
      options: [
        { label: 'Yes - Show Me the Videos', url: '/chat' },
        { label: 'No - Start My Journey', next: 'startMyJourney' },
        { label: 'Return Home', next: 'iHaveAnotherQuestion' },
      ],
    },
    startMyJourney: {
      messages:
        session && session?.user?.progress === 'assessment/friends-family'
          ? [
              <>Great. First, let’s see the real you…</>,
              <>
                Everything begins with understanding your natural strengths and the blind spots that
                have been holding you back…
              </>,
              <>This takes just a few minutes…</>,
              <>Are you ready to take your free personality assessment?</>,
            ]
          : [
              <>
                <b>SmartGuy:</b>
                <br />
                Great. First, let’s get your logged in or if you haven’t yet, signed up…
              </>,
            ],

      options:
        session && session?.user?.progress === 'assessment/friends-family'
          ? [
              { label: 'Yes - Show Me', next: 'yesShowMe' },
              { label: 'Not Yet', url: '/signin' },
            ]
          : [
              { label: 'Sign In', url: '/signin' },
              { label: 'Sign Up', next: '', url: '/signup' },
            ],
    },
    watchVideos: {
      messages: [<>Great. First, let’s get your logged in or if you haven’t yet, signed up…</>],
      options: [
        { label: 'Sign In', url: '/signin' },
        { label: 'Sign Up', next: '', url: '/signup' },
      ],
    },
    signin: {
      messages: [<>Great. First, let’s get your logged in or if you haven’t yet, signed up…</>],
      options: [
        { label: 'Sign In', url: '/signin' },
        { label: 'Sign Up', next: '', url: '/signup' },
      ],
    },
  };

  return (
    <main className="mt-5">
      <div className="absolute top-0 right-0 px-3 py-2 sm:px-5 sm:py-3 lg:px-10 lg:py-6">
        {!session ? (
          <div className="flex justify-between gap-x-3">
            <Link href="/signin">
              <Button className={cn('w-full rounded-full')}>Log in</Button>
            </Link>

            <Link href="/signup">
              <Button
                variant={'outline'}
                className={cn('border-dark-blue text-dark-blue w-full rounded-full')}
              >
                Sign up for free
              </Button>
            </Link>
          </div>
        ) : (
          <Button
            variant={'outline'}
            className={cn('border-dark-blue text-dark-blue w-full rounded-full')}
            onClick={() => signOut()}
          >
            Logout
          </Button>
        )}
      </div>

      <ScrollArea className="mt-5 min-h-60 overflow-y-auto bg-white">
        {/* <ScrollArea className="mt-5 min-h-[calc(100vh-370px)] overflow-y-auto bg-white"> */}
        <div className="mx-auto flex w-full max-w-3xl flex-col flex-wrap justify-start gap-y-4">
          {steps.map((step, index) => {
            const isActiveStep = index === steps.length - 1;
            const messages = flow[step].messages ?? [];

            const msgCount = messages.length;

            return (
              <React.Fragment key={index}>
                {messages.map((message, msgIndex) => {
                  // if this step is active, we compare msgIndex with revealedCount; otherwise reveal all previous steps' messages
                  const visible = !isActiveStep || msgIndex < revealedCount;

                  return (
                    <React.Fragment key={msgIndex}>
                      {visible ? (
                        <Message key={step + '-' + msgIndex} content={message} visible={visible} />
                      ) : (
                        ''
                      )}
                    </React.Fragment>
                  );
                })}

                {isActiveStep && isTyping && (
                  <div className="mb-2">
                    <TypingIndicator />
                  </div>
                )}

                {steps.length > 2 && <div ref={messagesEndRef} />}

                {(!isActiveStep || revealedCount >= msgCount) && (
                  <div className="flex flex-wrap justify-end gap-3">
                    {flow[step].options.map((option, index) => (
                      <div className="flex flex-wrap justify-start gap-3" key={index}>
                        {option.url ? (
                          <Link href={`${option.url}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue',
                                'rounded-full px-4 py-3 text-left text-sm break-words whitespace-normal',
                                'h-auto w-auto max-w-full'
                              )}
                            >
                              {option.label}
                            </Button>
                          </Link>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(
                              'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue',
                              'rounded-full px-4 py-3 text-left text-sm break-words whitespace-normal',
                              'h-auto w-auto max-w-full'
                            )}
                            onClick={() => {
                              setSteps((prev) => [...prev, option.next]);
                            }}
                          >
                            {option.label}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </ScrollArea>
    </main>
  );
}

const Message = ({ content }) => {
  return (
    <div className="message-shadow rounded-2xl border border-gray-100 bg-white px-6 py-3 text-gray-900 sm:px-8">
      {content}
    </div>
  );
};
